import { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSteps from '@/components/ProcessSteps';
import ContactFormDialog from '@/components/ContactFormDialog';
import { Eye, Shield, Bell, TrendingUp } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

export default function TrademarkMonitoring() {
  const [showDialog, setShowDialog] = useState(false);

  const benefits = [
    { icon: Eye, title: 'Constant Vigilance', description: 'Continuous monitoring of new trademark applications and registrations.' },
    { icon: Shield, title: 'Proactive Protection', description: 'Identify potential infringement before it becomes a major issue.' },
    { icon: Bell, title: 'Instant Alerts', description: 'Receive notifications when potentially conflicting marks are filed.' },
    { icon: TrendingUp, title: 'Market Intelligence', description: 'Stay informed about competitor trademark activity in your industry.' },
  ];

  const faqs = [
    { question: 'What is trademark monitoring?', answer: 'Trademark monitoring is an ongoing service that watches for new trademark applications and registrations that may conflict with your registered marks, helping you protect your brand proactively.' },
    { question: 'Why do I need trademark monitoring?', answer: 'Without monitoring, you may not discover infringing marks until they are already registered and being used, making enforcement more difficult and costly.' },
    { question: 'What databases are monitored?', answer: 'We monitor USPTO federal filings, state trademark databases, domain registrations, and other relevant sources depending on your needs.' },
    { question: 'What happens if a conflict is found?', answer: 'We alert you immediately and can help you file an opposition or take other appropriate legal action to protect your trademark rights.' },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85)' }}>
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C1810]/85 via-[#06214c]/75 to-[#A0522D]/70"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display' }}>Active Trademark Monitoring</h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">Protect your brand with continuous monitoring for potential trademark infringement.</p>
            <button onClick={() => setShowDialog(true)} className="btn-primary">Get Started Today</button>
          </div>
        </div>
      </section>
      <section className="section section-abstract-bg">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Why Choose Our Monitoring Services?</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Stay ahead of trademark infringement with professional monitoring.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="card text-center fade-in">
                  <div className="flex justify-center mb-4"><div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center"><Icon className="text-white" size={32} /></div></div>
                  <h3 className="text-xl font-bold text-[#06214c] mb-3" style={{ fontFamily: 'Playfair Display' }}>{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <ProcessSteps />

      {/* Pricing Section */}
      <PricingTable />

      <section className="section section-abstract-shapes">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#06214c] mb-4" style={{ fontFamily: 'Playfair Display' }}>Frequently Asked Questions</h2>
            <p className="text-lg text-[#2C1810] max-w-2xl mx-auto">Learn more about trademark monitoring services.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-[#D4A574]">
                  <AccordionTrigger className="text-left text-[#06214c] font-semibold hover:text-[#A0522D] py-4 text-[1.15rem]">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-4 text-[1.2rem]">{faq.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <TestimonialSlider autoSlide={true} />
      <section className="section bg-gradient-to-r from-[#06214c] to-[#A0522D] text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Start Monitoring Your Trademark Today</h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">Protect your brand investment with proactive trademark monitoring.</p>
          <button onClick={() => setShowDialog(true)} className="bg-white text-[#06214c] px-8 py-3 rounded-full font-bold hover:bg-[#FFF5E6] transition-all transform hover:scale-105 shadow-xl">Get Started Today</button>
        </div>
      </section>
      <ContactFormDialog open={showDialog} onOpenChange={setShowDialog} source="trademark_monitoring" />
      <Footer />
    </div>
  );
}